import { is_native_app } from './runtime'
import { browser_session_storage } from './browser-storage'
import type { GeolocationPlugin } from '@capacitor/geolocation'

export interface GeoPosition {
  lat: number
  lng: number
}

export interface GeoRequestResult {
  position: GeoPosition | null
  errorCode: number | null
  errorMessage: string | null
  permissionState: 'granted' | 'denied' | 'prompt' | null
  blocked: boolean
  unsupported: boolean
  insecureContext: boolean
}

const DEBUG_GPS_DEFAULT: GeoPosition = {
  // Centro aproximado de Aguas Claras/DF para smoke local
  lat: -15.8356,
  lng: -48.0204,
}
const DEBUG_GPS_SESSION_KEY = 'fidelidade_debug_geo'
const LAST_GEO_SESSION_KEY = 'fidelidade_last_geo'
const LAST_GEO_MAX_AGE_MS = 5 * 60 * 1000
const GEO_ERROR_NAMES: Record<number, string> = {
  1: 'PERMISSION_DENIED',
  2: 'POSITION_UNAVAILABLE',
  3: 'TIMEOUT',
}
// Tempo extra além do timeout nativo pra quem está respondendo um prompt real de permissão.
const GEO_PROMPT_GRACE_MS = 10000

function current_search(search?: string): string {
  if (typeof search === 'string') return search
  if (typeof window !== 'undefined') return window.location.search
  return ''
}

function parse_debug_number(value: string | null): number | null {
  if (!value) return null
  const num = Number(value.replace(',', '.'))
  return Number.isFinite(num) ? num : null
}

function read_stored_debug_geo(): { position: GeoPosition; custom: boolean } | null {
  const raw = browser_session_storage.getItem(DEBUG_GPS_SESSION_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { lat?: unknown; lng?: unknown; custom?: unknown }
    const lat = Number(parsed.lat)
    const lng = Number(parsed.lng)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    return {
      position: { lat, lng },
      custom: Boolean(parsed.custom),
    }
  } catch {
    return null
  }
}

function store_debug_geo(position: GeoPosition, custom: boolean): void {
  browser_session_storage.setItem(DEBUG_GPS_SESSION_KEY, JSON.stringify({ ...position, custom }))
}

function clear_stored_debug_geo(): void {
  browser_session_storage.removeItem(DEBUG_GPS_SESSION_KEY)
}

function read_stored_geo(max_age_ms = LAST_GEO_MAX_AGE_MS): GeoPosition | null {
  const raw = browser_session_storage.getItem(LAST_GEO_SESSION_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { lat?: unknown; lng?: unknown; ts?: unknown }
    const lat = Number(parsed.lat)
    const lng = Number(parsed.lng)
    const ts = Number(parsed.ts)
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(ts)) {
      browser_session_storage.removeItem(LAST_GEO_SESSION_KEY)
      return null
    }
    if (Date.now() - ts > max_age_ms) {
      browser_session_storage.removeItem(LAST_GEO_SESSION_KEY)
      return null
    }
    return { lat, lng }
  } catch {
    browser_session_storage.removeItem(LAST_GEO_SESSION_KEY)
    return null
  }
}

function store_geo_position(position: GeoPosition): void {
  browser_session_storage.setItem(LAST_GEO_SESSION_KEY, JSON.stringify({ ...position, ts: Date.now() }))
}

function clear_stored_geo(): void {
  browser_session_storage.removeItem(LAST_GEO_SESSION_KEY)
}

function permission_state(value: unknown): GeoRequestResult['permissionState'] {
  return value === 'granted' || value === 'denied' || value === 'prompt' ? value : null
}

function native_geo_error_code(message: string, blocked: boolean): number | null {
  if (blocked) return 1
  if (/timeout|tempo/i.test(message)) return 3
  if (/unavailable|disabled|indispon/i.test(message)) return 2
  return null
}

function geo_timeout_result(): GeoRequestResult {
  return {
    position: null,
    errorCode: 3,
    errorMessage: 'Tempo esgotado aguardando resposta de localização.',
    permissionState: null,
    blocked: false,
    unsupported: false,
    insecureContext: false,
  }
}

async function native_location_result(timeout_ms: number): Promise<GeoRequestResult> {
  let NativeGeolocation: GeolocationPlugin | null = null
  try {
    const mod = await import('@capacitor/geolocation')
    NativeGeolocation = mod.Geolocation
    const pos = await NativeGeolocation.getCurrentPosition({
      enableHighAccuracy: false,
      timeout: timeout_ms,
      maximumAge: 300000,
    })
    const position = { lat: pos.coords.latitude, lng: pos.coords.longitude }
    store_geo_position(position)
    return {
      position,
      errorCode: null,
      errorMessage: null,
      permissionState: 'granted',
      blocked: false,
      unsupported: false,
      insecureContext: false,
    }
  } catch (err) {
    const message = (err instanceof Error ? err.message : typeof err === 'string' ? err : '').trim()
    let state: GeoRequestResult['permissionState'] = null
    try {
      const status = await NativeGeolocation?.checkPermissions()
      state = permission_state(status?.location) || permission_state(status?.coarseLocation)
    } catch {
      // ignore
    }
    const blocked = state === 'denied' || /denied|permission|permiss/i.test(message)
    if (blocked) clear_stored_geo()
    return {
      position: null,
      errorCode: native_geo_error_code(message, blocked),
      errorMessage: message || null,
      permissionState: state,
      blocked,
      unsupported: /not implemented|not available/i.test(message),
      insecureContext: false,
    }
  }
}

// Guarda de deadline do caminho NATIVO (mesmo padrão idempotente do caminho web,
// ver settle() em request_location_result).
//
// No iOS, enquanto o diálogo de permissão está aberto — ou se o plugin não honrar
// o `timeout` que passamos — o await de getCurrentPosition não resolve NEM rejeita:
// fica pendente pra sempre. Sem esta guarda, a primeira tela do app trava com o
// botão em spinner e nenhum catch dispara (não há rejeição, só pendência).
//
// Resolvemos com resultado de erro (nunca rejeitamos) pra que a UI siga o fallback
// que já existe.
function request_native_location_result(timeout_ms: number): Promise<GeoRequestResult> {
  return new Promise((resolve) => {
    let settled = false
    const settle = (result: GeoRequestResult) => {
      if (settled) return
      settled = true
      clearTimeout(prompt_deadline)
      resolve(result)
    }
    const prompt_deadline = setTimeout(() => settle(geo_timeout_result()), timeout_ms + GEO_PROMPT_GRACE_MS)
    native_location_result(timeout_ms).then(settle, () => settle(geo_timeout_result()))
  })
}

function escape_html(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

async function query_geo_permission_hint(): Promise<'granted' | 'denied' | 'prompt' | null> {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator) || !navigator.permissions) return null
  try {
    const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
    return permission.state as 'granted' | 'denied' | 'prompt'
  } catch {
    return null
  }
}

export function get_debug_geo_position(search?: string): GeoPosition | null {
  const params = new URLSearchParams(current_search(search))
  const enabledParam = params.get('debug-gps')
  if (enabledParam !== null) {
    const enabled = enabledParam.trim().toLowerCase()
    if (!enabled || enabled === '0' || enabled === 'false' || enabled === 'off') {
      clear_stored_debug_geo()
      return null
    }

    const lat = parse_debug_number(params.get('debug-lat'))
    const lng = parse_debug_number(params.get('debug-lng'))
    if ((lat === null) !== (lng === null)) return null

    const position = lat !== null && lng !== null
      ? { lat, lng }
      : DEBUG_GPS_DEFAULT
    store_debug_geo(position, lat !== null && lng !== null)
    return position
  }

  return read_stored_debug_geo()?.position || null
}

export function is_debug_geo_enabled(search?: string): boolean {
  return get_debug_geo_position(search) !== null
}

export function debug_geo_label(search?: string): string {
  const params = new URLSearchParams(current_search(search))
  const enabledParam = params.get('debug-gps')
  const lat = parse_debug_number(params.get('debug-lat'))
  const lng = parse_debug_number(params.get('debug-lng'))
  if (enabledParam !== null && lat !== null && lng !== null) {
    return `GPS de smoke (${lat.toFixed(4)}, ${lng.toFixed(4)})`
  }
  const stored = read_stored_debug_geo()
  if (stored?.custom) {
    return `GPS de smoke (${stored.position.lat.toFixed(4)}, ${stored.position.lng.toFixed(4)})`
  }
  if (stored) return 'GPS de smoke: Aguas Claras - DF'
  if (lat !== null && lng !== null) return `GPS de smoke (${lat.toFixed(4)}, ${lng.toFixed(4)})`
  return 'GPS de smoke: Aguas Claras - DF'
}

export function get_cached_geo_position(max_age_ms = LAST_GEO_MAX_AGE_MS): GeoPosition | null {
  const debugPosition = get_debug_geo_position()
  if (debugPosition) return debugPosition
  return read_stored_geo(max_age_ms)
}

export function is_geo_secure_context(): boolean {
  if (typeof window === 'undefined') return true
  return window.isSecureContext
}

export function geo_insecure_context_message(): string {
  return 'Localização no navegador só funciona em HTTPS ou localhost. No iPhone, abra a URL HTTPS da aplicação.'
}

export function geo_failure_toast_message(result: GeoRequestResult): string {
  if (result.insecureContext) return geo_insecure_context_message()
  if (result.unsupported) return 'Seu navegador não suporta localização.'

  const base = result.blocked
    ? 'Localização bloqueada. Toque no cadeado na barra de endereço e libere a localização.'
    : 'Não foi possível obter sua localização agora. Tente novamente.'

  const details: string[] = []
  if (result.errorCode !== null) {
    const label = GEO_ERROR_NAMES[result.errorCode]
    details.push(label ? `código ${result.errorCode} (${label})` : `código ${result.errorCode}`)
  }
  if (result.errorMessage) details.push(`mensagem: ${escape_html(result.errorMessage)}`)
  if (result.permissionState) details.push(`permissão: ${escape_html(result.permissionState)}`)

  if (!details.length) return base
  return `${base}<br><small>Diagnóstico: ${details.join(' • ')}</small>`
}

export function request_location_result(timeout_ms = 10000): Promise<GeoRequestResult> {
  const debugPosition = get_debug_geo_position()
  if (debugPosition) {
    return Promise.resolve({
      position: debugPosition,
      errorCode: null,
      errorMessage: null,
      permissionState: 'granted',
      blocked: false,
      unsupported: false,
      insecureContext: false,
    })
  }
  if (is_native_app()) return request_native_location_result(timeout_ms)
  if (!is_geo_secure_context()) {
    return Promise.resolve({
      position: null,
      errorCode: null,
      errorMessage: null,
      permissionState: null,
      blocked: false,
      unsupported: false,
      insecureContext: true,
    })
  }
  if (!('geolocation' in navigator)) {
    return Promise.resolve({
      position: null,
      errorCode: null,
      errorMessage: null,
      permissionState: null,
      blocked: false,
      unsupported: true,
      insecureContext: false,
    })
  }

  return new Promise((resolve) => {
    let settled = false
    const settle = (result: GeoRequestResult) => {
      if (settled) return
      settled = true
      window.clearTimeout(prompt_deadline)
      resolve(result)
    }
    // O timeout nativo do getCurrentPosition só corre depois da permissão concedida;
    // prompt suprimido (quiet UI do Edge/Chrome desktop) deixaria a Promise pendente pra sempre.
    const prompt_deadline = window.setTimeout(() => {
      settle({
        position: null,
        errorCode: 3,
        errorMessage: 'Tempo esgotado aguardando resposta de localização.',
        permissionState: null,
        blocked: false,
        unsupported: false,
        insecureContext: false,
      })
    }, timeout_ms + GEO_PROMPT_GRACE_MS)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const position = { lat: pos.coords.latitude, lng: pos.coords.longitude }
        store_geo_position(position)
        settle({
          position,
          errorCode: null,
          errorMessage: null,
          permissionState: 'granted',
          blocked: false,
          unsupported: false,
          insecureContext: false,
        })
      },
      (error) => {
        void (async () => {
          if (error.code === error.PERMISSION_DENIED) clear_stored_geo()
          settle({
            position: null,
            errorCode: error.code ?? null,
            errorMessage: typeof error.message === 'string' && error.message.trim() ? error.message.trim() : null,
            permissionState: await query_geo_permission_hint(),
            blocked: error.code === error.PERMISSION_DENIED,
            unsupported: false,
            insecureContext: false,
          })
        })()
      },
      { enableHighAccuracy: false, timeout: timeout_ms, maximumAge: 300000 }
    )
  })
}

export function request_location(timeout_ms = 10000): Promise<GeoPosition | null> {
  return request_location_result(timeout_ms).then(result => result.position)
}

export async function ensure_geo_position(timeout_ms = 10000): Promise<GeoRequestResult> {
  const cached = get_cached_geo_position()
  if (cached) {
    return {
      position: cached,
      errorCode: null,
      errorMessage: null,
      permissionState: 'granted',
      blocked: false,
      unsupported: false,
      insecureContext: false,
    }
  }
  return request_location_result(timeout_ms)
}
